import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Radio, Typography, Alert } from "antd";
import moment from "moment";
import Echart from "./../chart/EChart";
import LineChart from "./../chart/LineChart";
import {
  get_user_according_to_date_rangeApi,
  get_new_users_according_to_date_range,
} from "../../api/admin.apis";

const { Title } = Typography;

const chartCardStyle = { height: "100%", borderRadius: 8 };

export const ChartsSection = ({ dateRange }) => {
  const [activeUsersByDate, setActiveUsersByDate] = useState([]);
  const [newUsersByDate, setNewUsersByDate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState("daily");

  const fetchAllData = useCallback(async (start, end) => {
    try {
      setLoading(true);
      setError(null);
      const startDate = moment(start).startOf("day").toISOString();
      const endDate = moment(end).endOf("day").toISOString();

      const [activeRes, newRes] = await Promise.all([
        get_user_according_to_date_rangeApi(startDate, endDate),
        get_new_users_according_to_date_range(startDate, endDate),
      ]);

      if (activeRes.data.status === "success") {
        let chartData = activeRes.data.data;

        if (viewType === "monthly") {
          const monthlyEmails = {};
          chartData.forEach((item) => {
            const month = moment(item.date).format("MMM YYYY");
            if (!monthlyEmails[month]) monthlyEmails[month] = new Set();
            (item.users || []).forEach((email) => {
              if (email) monthlyEmails[month].add(email);
            });
          });
          chartData = Object.entries(monthlyEmails).map(([date, emails]) => ({
            date,
            users: emails.size,
          }));
        } else {
          chartData = chartData.map((item) => ({
            date: item.date,
            users: item.unique_active_users,
          }));
        }

        setActiveUsersByDate(chartData);
      } else {
        setError("Failed to fetch active users data");
      }

      if (newRes.data.status === "success") {
        let chartData = newRes.data.data;

        if (viewType === "monthly") {
          const monthlyData = {};
          chartData.forEach((item) => {
            const month = moment(item.date).format("MMM YYYY");
            monthlyData[month] =
              (monthlyData[month] || 0) + item.new_users_count;
          });
          chartData = Object.entries(monthlyData).map(([date, users]) => ({
            date,
            new_users_count: users,
          }));
        }

        setNewUsersByDate(chartData);
      } else {
        setError("Failed to fetch new users data");
      }
    } catch (err) {
      console.error("Error fetching chart data:", err);
      setError(err.message || "Error fetching chart data");
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  useEffect(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return;
    fetchAllData(dateRange[0], dateRange[1]);
  }, [dateRange, fetchAllData]);

  const periodLabel = viewType === "monthly" ? "Monthly" : "Daily";

  return (
    <>
      <Row justify="end" style={{ marginBottom: 8 }}>
        <Radio.Group
          size="small"
          value={viewType}
          onChange={(e) => setViewType(e.target.value)}
          options={[
            { label: "Daily", value: "daily" },
            { label: "Monthly", value: "monthly" },
          ]}
          optionType="button"
        />
      </Row>
      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      ) : null}
      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <Card size="small" style={chartCardStyle} bodyStyle={{ padding: "8px 12px 0" }}>
            <Title level={5} style={{ margin: 0, fontSize: 14 }}>
              Active Users ({periodLabel})
            </Title>
            <Echart activeUsersByDate={activeUsersByDate} loading={loading} height={220} />
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card size="small" style={chartCardStyle} bodyStyle={{ padding: "8px 12px 0" }}>
            <Title level={5} style={{ margin: 0, fontSize: 14 }}>
              Users Overview ({periodLabel})
            </Title>
            <LineChart
              activeUsersByDate={activeUsersByDate}
              newUsersByDate={newUsersByDate}
              loading={loading}
              dateRange={dateRange}
              viewType={viewType}
              showTitle={false}
              height={220}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};
